import { useTranslation } from 'react-i18next';
import EnumSelect from '../../Fundamentals/Form/EnumSelect';
import FormField from '../../Fundamentals/Form/FormField';
import type { FormControlAriaProps } from '../../Fundamentals/Form/FormField';
import SkynetIconButton from '../../Fundamentals/IconButton/SkynetIconButton';
import TimeLengthFields from './TimeLengthFields';
import { packageTypes } from './packageConstants';
import { VossPackageTypeText } from '../../../Constants/VOSS/vossPackageType';
import type { PackageDraft, PackageDraftErrors } from './Packages.types';

interface ExternalIdInputProps extends FormControlAriaProps {
    value: string;
    onChange: (value: string) => void;
}

const ExternalIdInput = ({ value, onChange, ...ariaProps }: ExternalIdInputProps) => {
    const { t } = useTranslation();

    return (
        <span className="d-flex align-items-center gap-8">
            <input id="package-external-id" type="text" className="form-control" value={value} onChange={(e) => onChange(e.target.value)} {...ariaProps} />
            {/* only offered while empty, so a stray click can't replace an ID an integration already relies on */}
            {value.length === 0 && <SkynetIconButton icon="rotate" label={t('packages.generateGuid')} onClick={() => onChange(crypto.randomUUID())} />}
        </span>
    );
};

interface PackageBasicsFieldsProps {
    draft: PackageDraft;
    errors: Partial<PackageDraftErrors>;
    onChange: (change: Partial<PackageDraft>) => void;
    locked?: boolean;
}

const PackageBasicsFields = ({ draft, errors, onChange, locked = false }: PackageBasicsFieldsProps) => {
    const { t } = useTranslation();

    return (
        <>
            <div className="d-flex flex-wrap gap-16">
                <FormField label={t('common.name') ?? ''} htmlFor="package-name" required inline error={errors.name === true ? (t('packages.nameRequired') ?? '') : undefined}>
                    <input id="package-name" type="text" className="form-control" value={draft.name} onChange={(e) => onChange({ name: e.target.value })} />
                </FormField>
                <FormField label={t('packages.type') ?? ''} htmlFor="package-type" required inline error={errors.type === true ? (t('packages.typeRequired') ?? '') : undefined}>
                    <EnumSelect
                        id="package-type"
                        disabled={locked}
                        value={draft.type}
                        options={packageTypes}
                        getLabel={(packageType) => VossPackageTypeText[packageType]}
                        emptyLabel={draft.type === null ? (t('packages.selectType') ?? '') : undefined}
                        onChange={(type) => onChange({ type })}
                    />
                </FormField>
            </div>

            <FormField label={t('packages.externalId') ?? ''} htmlFor="package-external-id" help={t('packages.externalIdHelp') ?? ''}>
                <ExternalIdInput value={draft.externalId} onChange={(externalId) => onChange({ externalId })} />
            </FormField>

            <FormField
                label={t('packages.gracePeriod') ?? ''}
                htmlFor="package-grace-value"
                help={t('packages.gracePeriodHelp') ?? ''}
                error={errors.gracePeriod === true ? (t('packages.gracePeriodInvalid') ?? '') : undefined}
            >
                <TimeLengthFields id="package-grace-value" value={draft.gracePeriod} disabled={locked} onChange={(gracePeriod) => onChange({ gracePeriod })} />
            </FormField>

            <FormField label={t('packages.versionComment') ?? ''} htmlFor="package-comment" help={t('packages.versionCommentHelp') ?? ''}>
                <textarea id="package-comment" className="form-control" rows={3} disabled={locked} value={draft.comment} onChange={(e) => onChange({ comment: e.target.value })} />
            </FormField>
        </>
    );
};

export default PackageBasicsFields;
